import type { Distance, SpatialRectangle } from "@pdfking/math"
import { ToSpatial } from "@pdfking/math"
import type { LayerID } from "@pdfking/renderer-api"
import { CreateSingleton, Keys } from "@pdfking/tfunc"

import type { ImpositionNode, SheetSpec } from "#internal"
import type { Sheet } from "#public"

import { LayerList } from "../../data/LayerList"
import { CreateGroup } from "../../shapes/CreateGroup"
import { DrawLayout } from "./layout/DrawLayout"
import type { MediaSlots } from "./MediaSlots"

const ALL_LAYERS = Keys(LayerList) as LayerID[]

/**
 * The public sheets. Up front only what the viewer lays pages out with: each
 * sheet's page and bleed. A sheet is laid out the first time something draws
 * it or asks where its jobs landed, and drawn in its own frame: the plot
 * starts at its page.
 */
export const CreateSheets = (
  specs: SheetSpec[],
  media_of: ReturnType<typeof MediaSlots>
): Sheet[] =>
  specs.map(({ frame, bleed, destinations, divisions, repetition_number, layout: build, ctx }) => {
    const layout = CreateSingleton(build)
    const translate = { x: -frame.x as Distance, y: -frame.y as Distance }
    const position = ToSpatial(frame)
    // Every job's fill is a node of the layout: its box is that job's machine
    // sheet, as many times as the next job tiled it.
    const regions = CreateSingleton(() => {
      const found: Record<string, SpatialRectangle<Distance>[]> = {}
      const walk = (node: ImpositionNode) => {
        const id = node.data?.fill?.job.destination.id
        if (id)
          (found[id] ??= []).push({
            ...node.position,
            x: (node.position.x + translate.x) as Distance,
            y: (node.position.y + translate.y) as Distance,
          })
        node.children.forEach(walk)
      }
      walk(layout().root)
      return found
    })
    return {
      position,
      bleed,
      destinations,
      divisions,
      repetition_number,
      side: ctx.side,
      regions: id => regions()[id] ?? [],
      shapes: (drawn, layers = ALL_LAYERS) => {
        const { root } = layout()
        const plot = ctx.context_processor({
          ...ctx,
          canvas: root.position,
          root,
          media_of: media_of(drawn),
          plot_measures: true,
          plot_crossmarks: true,
        })
        return {
          ...position,
          bleed,
          shapes: CreateGroup(DrawLayout(layout(), plot, layers), { translate }),
        }
      },
    }
  })
